// ProjectDetailModal — opened from a ProjectChip under an assistant turn.
// Fetches endpoints from the data engine via the chat-server's
// /dataengine/* proxy and renders whatever subset is available:
//   - /projects/{name}                — base record (district/tenure/PSF era)
//   - /projects/{name}/details        — listing-source profile (specs, blurb)
//   - /projects/{name}/assets?kind=floor_plan — floor plan thumbnails
//   - /projects/{name}/floorplan      — extracted plan_types (per unit_type
//     metadata + source_image_sha256s linking back to assets). When
//     present, the floor plan gallery surfaces a row of unit_type filter
//     chips above the paginated grid; thumbnails not referenced by any
//     plan are reachable via an "Others" chip.
//
// Any 404 is treated as "not available for this project" rather than an
// error; the modal still renders the sections it could fill.

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { apiFetch } from "@/lib/api";

interface ProjectBase {
  name: string;
  slug?: string | null;
  street?: string | null;
  district?: string | null;
  property_types?: string[] | null;
  tenure?: string | null;
  market_segment?: string | null;
  postal?: string | null;
  lat?: number | null;
  lng?: number | null;
  planning_area?: string | null;
  txn_count?: number;
}

interface ProjectDetails {
  name: string;
  slug: string;
  category?: string;
  title: string;
  description?: string;
  specs_parsed?: Record<string, unknown>;
  videos?: unknown[];
  ingested_at?: string;
  synced_at?: string;
}

interface AssetRow {
  asset_sha256: string;
  kind: string;
  oss_key: string;
  url?: string;
  content_type: string;
  bytes: number;
}

interface AssetPage {
  name: string;
  slug?: string | null;
  rows: AssetRow[];
}

// One entry of floorplan_projects.plan_types[] (assembled by the data
// engine from the floorplan_units child table). Only the fields the UI
// actually reads are typed; everything else is ignored.
interface PlanType {
  unit_type: string;
  classification?: string | null;
  property_type?: string | null;
  n_storeys?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  total_area_sqm?: number | null;
  facing?: string | null;
  source_image_sha256s?: string[] | null;
}

interface FloorplanProject {
  name: string;
  slug: string;
  plan_types?: PlanType[] | null;
}

async function fetchOptional<T>(path: string): Promise<T | null> {
  try {
    const res = await apiFetch(path);
    if (res.status === 404) return null;
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

function specString(specs: Record<string, unknown> | undefined, key: string): string | null {
  if (!specs) return null;
  const v = specs[key];
  if (v == null) return null;
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  return null;
}

const FLOOR_PLANS_PER_PAGE = 9;
// Sentinel used as the filter chip key for the "Others" bucket — assets
// that no plan_types[] entry references. Anything that isn't a real
// unit_type works; this just needs to never collide with one. The
// double-underscore prefix mirrors React internal-key conventions.
const OTHERS_KEY = "__others__";

// Small chip strings shown next to a unit_type header — bedrooms,
// bathrooms, area, facing. Each is omitted when its source field is
// missing, so a sparse plan_types entry just shows a shorter chip row.
function unitChips(p: PlanType, t: (k: string) => string): string[] {
  const chips: string[] = [];
  if (typeof p.bedrooms === "number" && p.bedrooms > 0) {
    chips.push(`${p.bedrooms} ${t("projectModal.bedShort")}`);
  }
  if (typeof p.bathrooms === "number" && p.bathrooms > 0) {
    chips.push(`${p.bathrooms} ${t("projectModal.bathShort")}`);
  }
  if (typeof p.total_area_sqm === "number" && p.total_area_sqm > 0) {
    // Trim trailing .00 so 65.00 renders as "65 sqm".
    const a = Math.round(p.total_area_sqm * 10) / 10;
    chips.push(`${a} sqm`);
  }
  if (p.facing) chips.push(p.facing);
  return chips;
}

// Bedroom chip label — "Studio" for 0, otherwise "<n> BR" using the
// same short-form bedrooms localisation as unitChips.
function bedroomLabel(n: number, t: (k: string) => string): string {
  if (n === 0) return t("projectModal.studio");
  return `${n} ${t("projectModal.bedShort")}`;
}

export function ProjectDetailModal({ name, onClose }: { name: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [base, setBase] = useState<ProjectBase | null>(null);
  const [details, setDetails] = useState<ProjectDetails | null>(null);
  const [gallery, setGallery] = useState<AssetRow[]>([]);
  const [floorPlans, setFloorPlans] = useState<AssetRow[]>([]);
  // null when /floorplan returned 404 → fall back to flat paginated gallery.
  const [planTypes, setPlanTypes] = useState<PlanType[] | null>(null);
  const [loading, setLoading] = useState(true);
  // null = lightbox closed; otherwise the index into the displayAssets
  // sequence (the lightbox traverses the currently-filtered subset, in
  // grid display order).
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  // Floor plan gallery pagination — only the active page is mounted so
  // off-page images never download.
  const [planPage, setPlanPage] = useState(0);
  // Multi-select filters. Empty array = "All" (no narrowing). Filters
  // AND together — a plan must pass both to show. Unit keys are
  // unit_type strings (or OTHERS_KEY for the unmatched bucket); bedroom
  // values are integers (0 = studio). Only meaningful when planGroups
  // is non-null.
  const [selectedUnits, setSelectedUnits] = useState<string[]>([]);
  const [selectedBedrooms, setSelectedBedrooms] = useState<number[]>([]);

  // Group floor plan assets under their unit_type using
  // plan_types[].source_image_sha256s as the join key. Returns null when
  // there's no plan_types data (caller falls back to the flat gallery).
  // Empty unit buckets are dropped; thumbnails not referenced by any
  // plan land in a final "Others" bucket.
  const planGroups = useMemo(() => {
    if (!planTypes || planTypes.length === 0) return null;
    if (floorPlans.length === 0) return [];
    const shaToPlanIdx = new Map<string, number>();
    planTypes.forEach((p, i) => {
      for (const sha of p.source_image_sha256s ?? []) {
        // First plan wins on duplicates (rare, but possible for shared plates).
        if (!shaToPlanIdx.has(sha)) shaToPlanIdx.set(sha, i);
      }
    });
    const buckets: Array<{ plan: PlanType | null; assets: AssetRow[] }> =
      planTypes.map((p) => ({ plan: p, assets: [] }));
    const others: AssetRow[] = [];
    for (const a of floorPlans) {
      const idx = shaToPlanIdx.get(a.asset_sha256);
      if (idx !== undefined) buckets[idx].assets.push(a);
      else others.push(a);
    }
    const out = buckets.filter((b) => b.assets.length > 0);
    if (others.length > 0) out.push({ plan: null, assets: others });
    return out;
  }, [planTypes, floorPlans]);

  // Filter chip definitions — one chip per unit bucket plus the trailing
  // "Others" bucket. Built from planGroups so chip order matches the
  // server-side classification ordering (per_unit_plan first, then
  // floor_plate; ties broken by area). Null when grouping is unavailable.
  const filterOptions = useMemo(() => {
    if (!planGroups) return null;
    return planGroups.map((g) => ({
      key: g.plan ? g.plan.unit_type : OTHERS_KEY,
      label: g.plan?.unit_type ?? t("projectModal.floorPlansOthers"),
      plan: g.plan,
      assets: g.assets,
    }));
  }, [planGroups, t]);

  // Distinct bedroom counts that have at least one floor plan, sorted
  // ascending. The "Others" bucket has no plan and is intentionally
  // excluded — bedrooms are unknown for those assets, so they cannot
  // satisfy a bedroom predicate.
  const bedroomOptions = useMemo(() => {
    if (!planGroups) return null;
    const counts = new Map<number, number>();
    for (const g of planGroups) {
      const n = g.plan?.bedrooms;
      if (typeof n === "number") {
        counts.set(n, (counts.get(n) ?? 0) + g.assets.length);
      }
    }
    return Array.from(counts.entries())
      .sort(([a], [b]) => a - b)
      .map(([value, count]) => ({ value, count }));
  }, [planGroups]);

  // Drill-down: the unit facet visible to the user is narrowed by the
  // currently-active bedroom selection. This is intentionally one-way
  // (BR → unit). Going the other way would collapse the BR row to a
  // single value once a unit is picked, which carries no information.
  // "Others" has no plan and is excluded once any bedroom chip is on.
  const visibleUnitOptions = useMemo(() => {
    if (!filterOptions) return null;
    if (selectedBedrooms.length === 0) return filterOptions;
    const beds = new Set(selectedBedrooms);
    return filterOptions.filter(
      (o) => o.plan && typeof o.plan.bedrooms === "number" && beds.has(o.plan.bedrooms),
    );
  }, [filterOptions, selectedBedrooms]);

  // Currently-displayed floor plans — drives both the paginated grid and
  // the lightbox so prev/next traverses the same subset the user is
  // looking at. Empty selection arrays are treated as "no constraint",
  // so the default state (both empty) shows everything in grouped order.
  // The two filters AND together; an "Others" bucket asset only shows
  // when no bedroom filter is active.
  const displayAssets = useMemo<AssetRow[]>(() => {
    if (!filterOptions) return floorPlans;
    let opts = filterOptions;
    if (selectedUnits.length > 0) {
      const set = new Set(selectedUnits);
      opts = opts.filter((o) => set.has(o.key));
    }
    if (selectedBedrooms.length > 0) {
      const beds = new Set(selectedBedrooms);
      opts = opts.filter(
        (o) => o.plan && typeof o.plan.bedrooms === "number" && beds.has(o.plan.bedrooms),
      );
    }
    return opts.flatMap((o) => o.assets);
  }, [filterOptions, selectedUnits, selectedBedrooms, floorPlans]);

  // Drop stale unit selections — both when switching projects (the
  // unit_type may not exist) and when the bedroom filter changes (the
  // unit may have been narrowed out of the visible facet). Pruning
  // beats letting the AND result silently go empty — the user sees
  // exactly what's selected reflected in the chip row.
  useEffect(() => {
    if (selectedUnits.length === 0) return;
    if (!visibleUnitOptions) {
      setSelectedUnits([]);
      return;
    }
    const valid = new Set(visibleUnitOptions.map((o) => o.key));
    const next = selectedUnits.filter((k) => valid.has(k));
    if (next.length !== selectedUnits.length) setSelectedUnits(next);
  }, [visibleUnitOptions, selectedUnits]);
  useEffect(() => {
    if (selectedBedrooms.length === 0) return;
    if (!bedroomOptions) {
      setSelectedBedrooms([]);
      return;
    }
    const valid = new Set(bedroomOptions.map((o) => o.value));
    const next = selectedBedrooms.filter((b) => valid.has(b));
    if (next.length !== selectedBedrooms.length) setSelectedBedrooms(next);
  }, [bedroomOptions, selectedBedrooms]);
  useEffect(() => {
    setPlanPage(0);
  }, [selectedUnits, selectedBedrooms]);

  // When the user pages through the lightbox, snap the underlying gallery
  // page so closing the lightbox lands on a page that contains the last
  // floor plan they were looking at — not the page they originally opened.
  useEffect(() => {
    if (lightboxIdx === null) return;
    const target = Math.floor(lightboxIdx / FLOOR_PLANS_PER_PAGE);
    setPlanPage((p) => (p === target ? p : target));
  }, [lightboxIdx]);

  // Unified key handler — Esc on the lightbox closes it first; arrow keys
  // navigate the floor plan carousel; Esc on the modal (when lightbox is
  // already closed) closes the modal.
  useEffect(() => {
    const total = displayAssets.length;
    const onKey = (e: KeyboardEvent) => {
      if (lightboxIdx !== null) {
        if (e.key === "Escape") {
          e.stopPropagation();
          setLightboxIdx(null);
        } else if (e.key === "ArrowLeft") {
          setLightboxIdx((i) => (i === null || total === 0 ? null : (i - 1 + total) % total));
        } else if (e.key === "ArrowRight") {
          setLightboxIdx((i) => (i === null || total === 0 ? null : (i + 1) % total));
        }
        return;
      }
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, lightboxIdx, displayAssets.length]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setBase(null);
    setDetails(null);
    setGallery([]);
    setFloorPlans([]);
    setPlanTypes(null);
    setLightboxIdx(null);
    setPlanPage(0);
    setSelectedUnits([]);
    setSelectedBedrooms([]);
    const enc = encodeURIComponent(name);
    Promise.all([
      fetchOptional<ProjectBase>(`/dataengine/projects/${enc}`),
      fetchOptional<ProjectDetails>(`/dataengine/projects/${enc}/details`),
      fetchOptional<AssetPage>(`/dataengine/projects/${enc}/assets?kind=floor_plan`),
      fetchOptional<AssetPage>(`/dataengine/projects/${enc}/assets?kind=gallery`),
      fetchOptional<FloorplanProject>(`/dataengine/projects/${enc}/floorplan`),
    ]).then(([b, d, fp, g, plan]) => {
      if (cancelled) return;
      setBase(b);
      setDetails(d);
      setFloorPlans(fp?.rows ?? []);
      setGallery(g?.rows ?? []);
      setPlanTypes(plan?.plan_types ?? null);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [name]);

  const summary = useMemo(() => {
    const rows: Array<{ label: string; value: string }> = [];
    const specs = details?.specs_parsed;
    const dev = specString(specs, "developer");
    if (dev) rows.push({ label: t("projectModal.developer"), value: dev });
    const tenure = specString(specs, "tenure") || base?.tenure;
    if (tenure) rows.push({ label: t("projectModal.tenure"), value: tenure });
    const top = specString(specs, "top_year") || specString(specs, "top_raw");
    if (top) rows.push({ label: t("projectModal.topYear"), value: top });
    const units = specString(specs, "no_of_units");
    if (units) rows.push({ label: t("projectModal.totalUnits"), value: units });
    const district = base?.district || specString(specs, "district");
    if (district) rows.push({ label: t("projectModal.district"), value: `D${String(district).padStart(2, "0")}` });
    const region = specString(specs, "region");
    if (region) rows.push({ label: t("projectModal.region"), value: region });
    const segment = specString(specs, "segment") || base?.market_segment;
    if (segment) rows.push({ label: t("projectModal.segment"), value: segment });
    const address = specString(specs, "address") || base?.street;
    if (address) rows.push({ label: t("projectModal.address"), value: address });
    const types = base?.property_types;
    if (types && types.length > 0) rows.push({ label: t("projectModal.propertyType"), value: types.join(", ") });
    const txn = base?.txn_count;
    if (typeof txn === "number" && txn > 0) {
      rows.push({ label: t("projectModal.txnCount"), value: String(txn) });
    }
    return rows;
  }, [base, details, t]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={name}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-surface text-text rounded-xl border border-border overflow-hidden flex flex-col"
        style={{ maxWidth: 720, width: "100%", maxHeight: "85vh", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="m-0 text-[17px] font-semibold truncate" title={name}>{name}</h2>
            {details?.title && details.title !== name && (
              <p className="m-0 mt-0.5 text-[12px] text-text-muted truncate">{details.title}</p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            className="rounded p-1 text-text-muted hover:bg-surface-2 shrink-0"
            style={{ fontSize: 18, lineHeight: 1 }}
          >×</button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 flex-1">
          {loading ? (
            <div className="text-[13px] text-text-muted">{t("common.loading")}</div>
          ) : !base && !details ? (
            <div className="text-[13px] text-text-muted">{t("projectModal.notFound")}</div>
          ) : (
            <>
              {/* Hero banner — first available gallery image. Kept short
                  (~120px) so it sets context without dominating the modal. */}
              {gallery[0]?.url && (
                <div
                  className="mb-4 overflow-hidden rounded-md border border-border"
                  style={{ height: 120 }}
                >
                  <img
                    src={gallery[0].url}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
              )}

              {/* Spec grid */}
              {summary.length > 0 && (
                <div className="mb-4 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                  {summary.map(({ label, value }) => (
                    <div key={label} className="min-w-0">
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-text-muted">{label}</div>
                      <div className="text-[13px] text-text truncate" title={value}>{value}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Floor plans — single paginated grid driven by
                  displayAssets. When floorplan extraction data exists,
                  a row of unit_type filter chips above the grid narrows
                  the display (and the lightbox sequence) to one bucket
                  at a time. Chips are absent for projects without
                  extracted plan_types, which falls back cleanly to the
                  un-filtered paginated gallery.

                  An additional metadata line surfaces the active unit's
                  bedrooms / bathrooms / area / facing whenever a real
                  unit_type chip is selected. */}
              {floorPlans.length > 0 && (() => {
                const totalPages = Math.max(1, Math.ceil(displayAssets.length / FLOOR_PLANS_PER_PAGE));
                const safePage = Math.min(planPage, totalPages - 1);
                const start = safePage * FLOOR_PLANS_PER_PAGE;
                const pageItems = displayAssets.slice(start, start + FLOOR_PLANS_PER_PAGE);
                // Surface unit metadata only when exactly one real
                // unit_type chip is selected — multi-select makes a
                // single bedrooms/area line ambiguous.
                const activePlan =
                  selectedUnits.length === 1 && filterOptions
                    ? filterOptions.find((o) => o.key === selectedUnits[0])?.plan ?? null
                    : null;
                const activeChips = activePlan ? unitChips(activePlan, t) : [];
                // "All" chip count tracks the visible unit facet so the
                // header reflects whatever the user can currently click
                // — when bedroom narrows the row, the All total drops
                // to match the narrowed pool.
                const totalCount = visibleUnitOptions
                  ? visibleUnitOptions.reduce((sum, o) => sum + o.assets.length, 0)
                  : floorPlans.length;
                const toggle = <T,>(arr: T[], v: T): T[] =>
                  arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
                return (
                  <div className="mb-4">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="text-[11px] font-semibold uppercase tracking-widest text-text-muted">
                        {t("projectModal.floorPlans")} ({displayAssets.length})
                      </div>
                      {totalPages > 1 && (
                        <div className="flex items-center gap-1.5 text-[11px] text-text-muted">
                          <button
                            type="button"
                            onClick={() => setPlanPage((p) => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            className="rounded-md border border-border bg-surface-2 px-2 py-0.5 hover:bg-surface-3 disabled:opacity-40 disabled:hover:bg-surface-2"
                            aria-label={t("projectModal.prev")}
                          >‹</button>
                          <span className="tabular-nums px-1">{safePage + 1} / {totalPages}</span>
                          <button
                            type="button"
                            onClick={() => setPlanPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={safePage >= totalPages - 1}
                            className="rounded-md border border-border bg-surface-2 px-2 py-0.5 hover:bg-surface-3 disabled:opacity-40 disabled:hover:bg-surface-2"
                            aria-label={t("projectModal.next")}
                          >›</button>
                        </div>
                      )}
                    </div>

                    {bedroomOptions && bedroomOptions.length > 0 && (
                      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] uppercase tracking-widest text-text-muted mr-1">
                          {t("projectModal.bedroomsLabel")}
                        </span>
                        <FilterChip
                          active={selectedBedrooms.length === 0}
                          onClick={() => setSelectedBedrooms([])}
                          label={t("projectModal.filterAll")}
                          count={bedroomOptions.reduce((s, o) => s + o.count, 0)}
                        />
                        {bedroomOptions.map((o) => (
                          <FilterChip
                            key={o.value}
                            active={selectedBedrooms.includes(o.value)}
                            onClick={() => setSelectedBedrooms((prev) => toggle(prev, o.value))}
                            label={bedroomLabel(o.value, t)}
                            count={o.count}
                          />
                        ))}
                      </div>
                    )}
                    {visibleUnitOptions && visibleUnitOptions.length > 0 && (
                      <div className="mb-2 flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] uppercase tracking-widest text-text-muted mr-1">
                          {t("projectModal.unitsLabel")}
                        </span>
                        <FilterChip
                          active={selectedUnits.length === 0}
                          onClick={() => setSelectedUnits([])}
                          label={t("projectModal.filterAll")}
                          count={totalCount}
                        />
                        {visibleUnitOptions.map((o) => (
                          <FilterChip
                            key={o.key}
                            active={selectedUnits.includes(o.key)}
                            onClick={() => setSelectedUnits((prev) => toggle(prev, o.key))}
                            label={o.label}
                            count={o.assets.length}
                          />
                        ))}
                      </div>
                    )}

                    {activeChips.length > 0 && (
                      <div className="mb-2 text-[11px] text-text-muted">
                        {activeChips.join(" · ")}
                      </div>
                    )}

                    {displayAssets.length === 0 ? (
                      <div className="rounded border border-border bg-surface-2 px-3 py-4 text-center text-[12px] text-text-muted">
                        {t("projectModal.filterEmpty")}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {pageItems.map((a, i) => {
                          const idx = start + i;
                          // Only the OSS-composed url is rendered. The upstream
                          // listing-source URL is never returned by the API.
                          if (!a.url) return null;
                          return (
                            <button
                              key={a.asset_sha256}
                              type="button"
                              onClick={() => setLightboxIdx(idx)}
                              className="block overflow-hidden rounded border border-border bg-surface-2 hover:border-emerald-500"
                              style={{ aspectRatio: "1 / 1" }}
                              aria-label={t("projectModal.zoom")}
                            >
                              <img
                                src={a.url}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                className="h-full w-full object-cover"
                              />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Marketing description */}
              {details?.description && (
                <div className="mb-4">
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-text-muted">
                    {t("projectModal.about")}
                  </div>
                  <p className="text-[13px] leading-[1.6] text-text-sec whitespace-pre-wrap">
                    {details.description}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-2.5 text-[11px] text-text-muted">
          <div className="truncate">
            {t("projectModal.sourceLabel")}: {t("projectModal.sourceValue")}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onClose}
              className="rounded-md border border-border bg-surface-2 px-2.5 py-1 text-[11px] text-text-sec hover:bg-surface-3"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox — sits on top of the modal (z-index 10000 > 9999),
          backdrop click + Esc + arrow keys close / navigate.
          `displayAssets` is the currently-filtered subset shown in the
          grid, so prev/next stays inside the active unit_type chip. */}
      {lightboxIdx !== null && displayAssets[lightboxIdx]?.url && (
        <FloorPlanLightbox
          assets={displayAssets}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx((i) => (i === null ? null : (i - 1 + displayAssets.length) % displayAssets.length))}
          onNext={() => setLightboxIdx((i) => (i === null ? null : (i + 1) % displayAssets.length))}
        />
      )}
    </div>,
    document.body,
  );
}

// FilterChip — one toggleable pill in the floor-plan filter row.
// Active state uses the same emerald accent the gallery hover uses,
// so the user can tell which bucket the grid is currently showing.
function FilterChip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        "rounded-full border px-2.5 py-0.5 text-[11px] transition-colors " +
        (active
          ? "border-emerald-500 bg-emerald-500/15 text-text"
          : "border-border bg-surface-2 text-text-sec hover:bg-surface-3")
      }
    >
      <span className="font-medium">{label}</span>
      <span className="ml-1 tabular-nums text-text-muted">({count})</span>
    </button>
  );
}

function FloorPlanLightbox({ assets, index, onClose, onPrev, onNext }: {
  assets: AssetRow[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const a = assets[index];
  if (!a?.url) return null;
  const multi = assets.length > 1;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="floor plan"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(0,0,0,0.88)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 32,
      }}
    >
      {/* Close (top-right) */}
      <button
        type="button"
        onClick={onClose}
        aria-label="close"
        className="absolute top-3 right-3 rounded-full bg-black/40 px-2.5 py-1 text-white hover:bg-black/60"
        style={{ fontSize: 18, lineHeight: 1 }}
      >×</button>

      {/* Counter (top-left) */}
      {multi && (
        <div className="absolute top-3 left-3 rounded-md bg-black/40 px-2.5 py-1 text-[12px] text-white tabular-nums">
          {index + 1} / {assets.length}
        </div>
      )}

      {/* Prev */}
      {multi && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          aria-label="previous"
          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 px-3 py-2 text-white hover:bg-black/60"
        >‹</button>
      )}

      {/* Image — object-contain so the whole plan is visible. eager
          because this IS the focal image; decoding async to avoid
          blocking the click that opened the lightbox. */}
      <img
        src={a.url}
        alt=""
        loading="eager"
        decoding="async"
        style={{ maxWidth: "92vw", maxHeight: "88vh", objectFit: "contain", display: "block" }}
      />

      {/* Next */}
      {multi && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          aria-label="next"
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 px-3 py-2 text-white hover:bg-black/60"
        >›</button>
      )}
    </div>
  );
}
